import { ObjectId } from "@novorender/data-js-api";
import { createContext, Dispatch, MutableRefObject } from "react";

import { SelectionSet } from "utils/selectionSet";

export const initialState = {
    ids: SelectionSet.EMPTY,
};

export type State = typeof initialState;

export enum ActionTypes {
    Add,
    Remove,
    SetIds,
}

function add(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Add as const,
        ids,
    };
}

function remove(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Remove as const,
        ids,
    };
}

function setIds(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.SetIds as const,
        ids,
    };
}

export const actions = { add, remove, setIds };

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchShown = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchShown>(undefined as any);

/**
 * Add and remove only ever grow or shrink the set, so an unchanged size means an unchanged set.
 * Comparing size rather than reference matters because `withAdded` allocates even when every id
 * was already present, and the model tree memoizes on this state's identity.
 */
function unchangedOr(state: State, ids: SelectionSet): State {
    return ids.size === state.ids.size ? state : { ids };
}

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            return unchangedOr(state, state.ids.withAdded(action.ids));
        }
        case ActionTypes.Remove: {
            return unchangedOr(state, state.ids.withRemoved(action.ids));
        }
        case ActionTypes.SetIds: {
            return { ids: SelectionSet.fromIds(action.ids) };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
